import type { AppUpdater, DeployedRelease } from '@/application/ports/AppUpdater';

/**
 * Moving an open page onto a newly deployed build.
 *
 * Why phones stayed on old builds: the worker in `src/sw.ts` answers every
 * navigation with the `index.html` it precached, which is right, because it is
 * what opens the app in a dead spot. A new deploy installs a new worker, and a
 * new worker WAITS until no page is using the old one. The app registered in
 * prompt mode and never prompted, so it waited for every window to close, and
 * an app on a home screen is resumed rather than closed, for days on iOS. The
 * server's headers were never the problem: index.html and sw.js are already
 * no-store (deploy/nginx.conf).
 *
 * This asks the server which build it has, lets the worker fetch the new one as
 * soon as it exists, and hands over to it when the person taps Update.
 */

/** Where vite-plugin-pwa writes the worker built from `src/sw.ts`. */
export const SERVICE_WORKER_URL = '/sw.js';

/** Written by the build, next to index.html. See `vite.config.ts`. */
export const VERSION_URL = '/version.json';

/**
 * The build this tab last tried to update FROM. Finding it equal to the running
 * build means the tap reloaded onto the same build, so the next tap takes the
 * long way round instead of trying the same thing twice.
 */
const ATTEMPT_KEY = 'ceramica:update-from';

/**
 * The build this tab last took the long way FROM. It stops the automatic second
 * attempt (see `continuePending`) from ever running twice for one build.
 */
const LONG_WAY_KEY = 'ceramica:update-long-way-from';

/** Photographs never change once uploaded, so they survive an update. */
const KEPT_CACHES = ['ceramica-photos'];

const FIND_TIMEOUT_MS = 10_000;
const INSTALL_TIMEOUT_MS = 20_000;
const TAKE_OVER_TIMEOUT_MS = 10_000;

function supported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

/**
 * Registers the worker once the page has loaded, so its precache download does
 * not compete with the first screen.
 *
 * `updateViaCache: 'none'` has the browser fetch the worker script past its own
 * HTTP cache on every update check, whatever a proxy in front of nginx does.
 */
export async function registerServiceWorker(): Promise<void> {
  if (!supported()) return;

  if (document.readyState !== 'complete') {
    await new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
  }

  try {
    await navigator.serviceWorker.register(SERVICE_WORKER_URL, {
      scope: '/',
      updateViaCache: 'none',
    });
  } catch {
    // No worker means no offline shell, not a broken app: every request still
    // goes to the network, and the version check still offers updates.
  }
}

export class ServiceWorkerAppUpdater implements AppUpdater {
  private applying = false;

  /** True once another tab has already swapped this page's worker for a newer one. */
  private replaced = false;

  /**
   * The release the last readable `/version.json` named, or null when it named
   * none. An answer that could not be read leaves it as it was. The request of
   * 5 October 2026 (third), section 8.
   */
  private release: DeployedRelease | null = null;

  constructor(
    private readonly current: string,
    private readonly reload: () => void = () => window.location.reload(),
  ) {
    if (!supported()) return;

    const hadController = navigator.serviceWorker.controller !== null;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (hadController) this.replaced = true;
    });
  }

  async deployedVersion(): Promise<string | null> {
    try {
      // The query string as well as no-store, for any cache between here and
      // nginx that does not honour the header.
      const response = await fetch(`${VERSION_URL}?t=${String(Date.now())}`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;

      // A server without the file answers with index.html and a 200, so the
      // body is checked rather than the status alone.
      const body: unknown = await response.json();
      const { version, release, releasedOn } = (body ?? {}) as {
        version?: unknown;
        release?: unknown;
        releasedOn?: unknown;
      };
      if (typeof version !== 'string' || version === '') return null;

      // What that build is called, kept for the offer. A file written before
      // releases had numbers names none, and then there is nothing to say.
      this.release =
        typeof release === 'string' && release !== ''
          ? { release, releasedOn: typeof releasedOn === 'string' && releasedOn !== '' ? releasedOn : null }
          : null;
      return version;
    } catch {
      return null;
    }
  }

  deployedRelease(): DeployedRelease | null {
    return this.release;
  }

  checkWorker(): void {
    if (!supported()) return;

    void navigator.serviceWorker
      .getRegistration()
      .then((registration) => registration?.update())
      .catch(() => undefined);
  }

  onNewWorker(listener: () => void): () => void {
    if (!supported()) return () => undefined;

    const container = navigator.serviceWorker;
    const hadController = container.controller !== null;
    const cleanups: Array<() => void> = [];
    let active = true;

    // Another tab tapped Update: the worker serving this page is now newer than
    // the page, and its next lazy load may ask for a file that no longer exists.
    const onControllerChange = () => {
      if (active && hadController && !this.applying) listener();
    };
    container.addEventListener('controllerchange', onControllerChange);
    cleanups.push(() => container.removeEventListener('controllerchange', onControllerChange));

    void container
      .getRegistration()
      .then((registration) => {
        if (!active || registration === undefined) return;

        // Only an update: the very first worker installs with no page to replace.
        const report = () => {
          if (active && registration.waiting !== null && container.controller !== null) listener();
        };

        const onFound = () => {
          const worker = registration.installing;
          worker?.addEventListener('statechange', () => {
            if (worker.state === 'installed') report();
          });
        };

        report();
        registration.addEventListener('updatefound', onFound);
        cleanups.push(() => registration.removeEventListener('updatefound', onFound));
      })
      .catch(() => undefined);

    return () => {
      active = false;
      for (const cleanup of cleanups) cleanup();
    };
  }

  /**
   * True when the last Update in this tab reloaded and the page came back on the
   * same build, and the long way has not been tried for it yet. People had to
   * tap Update twice for exactly this; now the page carries on by itself, once.
   */
  continuePending(): boolean {
    return readKey(ATTEMPT_KEY) === this.current && readKey(LONG_WAY_KEY) !== this.current;
  }

  async apply(): Promise<boolean> {
    // Every way onto the new build needs the server. Offline, a reload would
    // show the old build again at best and the browser's error page at worst.
    if ((await this.deployedVersion()) === null) return false;

    this.applying = true;
    const triedAlready = readKey(ATTEMPT_KEY) === this.current;
    writeKey(ATTEMPT_KEY, this.current);

    const registration = supported()
      ? await navigator.serviceWorker.getRegistration().catch(() => undefined)
      : undefined;

    if (registration !== undefined) {
      const next = triedAlready ? null : await newWorker(registration);

      if (next !== null) {
        // The worker clears the caches the new build must not inherit as it
        // activates. See the activate handler in src/sw.ts.
        await takeOver(next);
        this.reload();
        return true;
      }

      if (triedAlready || !this.replaced) {
        /*
         * The server has a newer build and no newer worker turned up, or the
         * last tap reloaded onto the same build. The worker in charge would
         * answer the reload from its precache, so it is taken out of the way
         * and the page comes from the network. The next load registers the
         * current worker again. The outbox is in IndexedDB, not in a cache, so
         * no queued work goes with it.
         */
        writeKey(LONG_WAY_KEY, this.current);
        await registration.unregister().catch(() => false);
        await dropCaches();
      }
    }

    this.reload();
    return true;
  }
}

/**
 * The NEWEST worker for this registration.
 *
 * It always asks the server first, even with a worker already waiting: the
 * waiting one may be from the deploy before last, and handing over to it
 * reloaded the page onto a build that was already out of date, so the banner
 * came straight back and asked for a second tap. A worker still installing is
 * newer than one waiting, so it is waited for in preference.
 */
async function newWorker(registration: ServiceWorkerRegistration): Promise<ServiceWorker | null> {
  try {
    await withTimeout(registration.update(), FIND_TIMEOUT_MS);
  } catch {
    // The check itself failed. Whatever it had already started is read below.
  }

  const installing = registration.installing;
  if (installing !== null) return (await installed(installing)) ?? registration.waiting;
  return registration.waiting;
}

/** Resolves with the worker once it has finished installing, or null if it fails. */
function installed(worker: ServiceWorker): Promise<ServiceWorker | null> {
  return new Promise((resolve) => {
    const settle = (value: ServiceWorker | null) => {
      window.clearTimeout(timer);
      worker.removeEventListener('statechange', onState);
      resolve(value);
    };
    const onState = () => {
      if (worker.state === 'installed') settle(worker);
      else if (worker.state !== 'installing') settle(null);
    };
    const timer = window.setTimeout(() => settle(null), INSTALL_TIMEOUT_MS);
    worker.addEventListener('statechange', onState);
  });
}

/**
 * Tells the waiting worker to activate, and waits until it controls this page.
 * The timeout is there so a worker that never answers cannot leave the button
 * spinning: the reload that follows lands on it anyway once it has activated.
 */
function takeOver(worker: ServiceWorker): Promise<void> {
  return new Promise((resolve) => {
    const container = navigator.serviceWorker;
    const done = () => {
      window.clearTimeout(timer);
      container.removeEventListener('controllerchange', done);
      resolve();
    };
    const timer = window.setTimeout(done, TAKE_OVER_TIMEOUT_MS);
    container.addEventListener('controllerchange', done);
    worker.postMessage({ type: 'SKIP_WAITING' });
  });
}

async function dropCaches(): Promise<void> {
  if (typeof caches === 'undefined') return;

  try {
    const names = await caches.keys();
    await Promise.all(names.filter((name) => !KEPT_CACHES.includes(name)).map((name) => caches.delete(name)));
  } catch {
    // Storage the browser will not let us clear is not a reason to stay on the
    // old build; the unregistered worker already cannot answer the reload.
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('timed out')), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

/** Session storage can throw (private mode, storage off), and then there is simply no memory. */
function readKey(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key: string, version: string): void {
  try {
    window.sessionStorage.setItem(key, version);
  } catch {
    // As above.
  }
}
